import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LayersIcon from '@mui/icons-material/Layers';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import {
  getCatalog,
  searchNetwork,
  listScenarios,
  deleteScenario,
  type NetworkCatalogFeed,
  type NetworkVersionSummary,
} from '../../../services/network/networkApi';
import { useNetworkBrowse } from '../../../context/NetworkBrowseContext';
import { useOpenNetworkVersion } from '../../../hooks/useOpenNetworkVersion';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../services/eventTaxonomy';
import { MAP_UI_RIGHT_RAIL_WIDTH_PX, MAP_UI_TOP_BAR_HEIGHT_PX } from '../mapUiLayout';

/**
 * Feed picker + network-wide search for the hosted-network display (PRD-04 FR6/5.1). A
 * self-contained, absolutely-positioned panel (no modal backdrop) so the map stays
 * interactive while picking + panning. Lists the entitlement-filtered catalog; once a feed
 * is selected, a search box queries that network's routes + stops server-side and a result
 * click zooms/focuses + highlights it on the map (via `NetworkBrowseContext`).
 */
interface Props {
  open: boolean;
  onClose: () => void;
  selectedFeedId: string | null;
  onSelectFeed: (feedId: string | null) => void;
  statusText?: string | null;
}

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 15;

interface SearchResults {
  routes: Array<Record<string, unknown>>;
  stops: Array<Record<string, unknown>>;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : v == null ? undefined : String(v));
const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

const HostedNetworkPickerDrawer: React.FC<Props> = ({ open, onClose, selectedFeedId, onSelectFeed, statusText }) => {
  const { t } = useTranslation(['mapCore']);
  const { requestZoomToRoute, focusStop, scenarioId, selectVersion } = useNetworkBrowse();
  const openVersion = useOpenNetworkVersion();
  const [openingId, setOpeningId] = useState<string | null>(null);

  const handleOpenVersion = async (id: string) => {
    if (!selectedFeedId) return;
    setOpeningId(id);
    try {
      const res = await openVersion(id);
      selectVersion(id);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_version_opened, {
        feed_id: selectedFeedId,
        edited_count: res.editedCount,
        new_count: res.newCount,
        timestamp: new Date().toISOString(),
      });
      onClose(); // routes now render on the map (Fixed Routes layer) over the base
    } catch {
      /* surfaced via console; leave the picker open so the user can retry */
    } finally {
      setOpeningId(null);
    }
  };

  const [feeds, setFeeds] = useState<NetworkCatalogFeed[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit versions (scenarios) of the selected feed — the versions dropdown (PRD-05c).
  const [versions, setVersions] = useState<NetworkVersionSummary[]>([]);

  useEffect(() => {
    if (!open || !selectedFeedId) {
      setVersions([]);
      return undefined;
    }
    const ctl = new AbortController();
    listScenarios(selectedFeedId, ctl.signal)
      .then(setVersions)
      .catch(() => { if (!ctl.signal.aborted) setVersions([]); });
    return () => ctl.abort();
  }, [open, selectedFeedId]);

  const handleDeleteVersion = async (id: string) => {
    if (!selectedFeedId) return;
    try {
      await deleteScenario(selectedFeedId, id);
      setVersions((prev) => prev.filter((v) => v.scenarioId !== id));
      if (scenarioId === id) selectVersion(null);
    } catch { /* best-effort; leave the list as-is on failure */ }
  };

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getCatalog(controller.signal)
      .then(setFeeds)
      .catch(() => {
        if (!controller.signal.aborted) setError(t('transitNetworks.loadError'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, t]);

  // Reset the search when the feed changes / the panel closes.
  useEffect(() => {
    setQuery('');
    setDebouncedQuery('');
    setResults(null);
    setSearchError(false);
  }, [selectedFeedId, open]);

  // Debounce the query.
  useEffect(() => {
    const tmo = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tmo);
  }, [query]);

  // Run the network search.
  useEffect(() => {
    if (!selectedFeedId || !debouncedQuery) {
      setResults(null);
      setSearching(false);
      return undefined;
    }
    const ctl = new AbortController();
    setSearching(true);
    setSearchError(false);
    searchNetwork(selectedFeedId, debouncedQuery, 'all', { limit: SEARCH_LIMIT, signal: ctl.signal })
      .then((res) => {
        setResults(res);
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_search, {
          feed_id: selectedFeedId,
          query_length: debouncedQuery.length,
          route_results: res.routes?.length ?? 0,
          stop_results: res.stops?.length ?? 0,
          timestamp: new Date().toISOString(),
        });
      })
      .catch(() => {
        if (!ctl.signal.aborted) setSearchError(true);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setSearching(false);
      });
    return () => ctl.abort();
  }, [selectedFeedId, debouncedQuery]);

  const hasQuery = debouncedQuery.length > 0;
  const routeResults = results?.routes ?? [];
  const stopResults = results?.stops ?? [];
  const noResults = hasQuery && !searching && !searchError && routeResults.length === 0 && stopResults.length === 0;

  const resultsList = useMemo(
    () => (
      <List dense disablePadding>
        {routeResults.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>
            {t('transitNetworks.routesHeading')}
          </ListSubheader>
        )}
        {routeResults.map((r, i) => {
          const routeId = str(r.routeId);
          if (!routeId) return null;
          const shortName = str(r.routeShortName);
          const longName = str(r.routeLongName);
          return (
            <ListItemButton key={`r-${routeId}-${i}`} onClick={() => requestZoomToRoute(routeId)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <DirectionsBusIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={shortName || routeId}
                secondary={longName}
                primaryTypographyProps={{ noWrap: true, variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
              />
            </ListItemButton>
          );
        })}
        {stopResults.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>
            {t('transitNetworks.stopsHeading')}
          </ListSubheader>
        )}
        {stopResults.map((s, i) => {
          const stopId = str(s.stopId);
          const lat = num(s.stopLat);
          const lng = num(s.stopLon);
          if (!stopId || lat == null || lng == null) return null;
          const name = str(s.stopName);
          return (
            <ListItemButton key={`s-${stopId}-${i}`} onClick={() => focusStop({ stopId, lat, lng, name })}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PlaceIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={name || stopId}
                primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              />
            </ListItemButton>
          );
        })}
      </List>
    ),
    [routeResults, stopResults, requestZoomToRoute, focusStop, t],
  );

  if (!open) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        // Sit BESIDE the fixed right rail + BELOW the top bar (fixed = same
        // coordinate space as the rail, so it can't slide under it).
        position: 'fixed',
        top: MAP_UI_TOP_BAR_HEIGHT_PX + 8,
        right: MAP_UI_RIGHT_RAIL_WIDTH_PX + 8,
        width: 340,
        maxHeight: `calc(100vh - ${MAP_UI_TOP_BAR_HEIGHT_PX + 24}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 2150, // above the right rail (2100); below modal drawer papers (2201)
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HubIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('transitNetworks.title')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('transitNetworks.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      {/* Network-wide search — only once a feed is on the map (PRD-04 5.1). */}
      {selectedFeedId && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('transitNetworks.searchPlaceholder')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Versions dropdown — Base + edit versions of the selected feed (PRD-05c). */}
      {selectedFeedId && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('transitNetworks.versionsHeading', 'Versions')}
              </Typography>
            </Box>
            <List dense disablePadding>
              <ListItemButton selected={!scenarioId} onClick={() => selectVersion(null)} sx={{ borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LayersIcon fontSize="small" color={!scenarioId ? 'primary' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary={t('transitNetworks.baseVersion', 'Base network')}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
              </ListItemButton>
              {versions.map((v) => {
                const s = v.summary;
                const selected = scenarioId === v.scenarioId;
                const meta = s
                  ? t('transitNetworks.versionDiff', {
                      defaultValue: '{{edited}} edited · {{added}} new · {{removed}} removed',
                      edited: s.editedCount, added: s.addedCount, removed: s.removedCount,
                    })
                  : '';
                return (
                  <ListItemButton
                    key={v.scenarioId}
                    selected={selected}
                    onClick={() => selectVersion(v.scenarioId)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <HistoryIcon fontSize="small" color={selected ? 'primary' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={v.name}
                      secondary={meta}
                      primaryTypographyProps={{ noWrap: true, variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                    />
                    <Tooltip title={t('transitNetworks.openVersion', 'Open in editor')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={openingId === v.scenarioId}
                          aria-label={t('transitNetworks.openVersion', 'Open in editor')}
                          onClick={(e) => { e.stopPropagation(); handleOpenVersion(v.scenarioId); }}
                        >
                          {openingId === v.scenarioId
                            ? <CircularProgress size={16} />
                            : <EditNoteIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={t('transitNetworks.deleteVersion', 'Delete version')}
                      onClick={(e) => { e.stopPropagation(); handleDeleteVersion(v.scenarioId); }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </>
      )}

      {loading && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {error && (
        <Typography color="error" variant="body2" sx={{ p: 2 }}>
          {error}
        </Typography>
      )}
      {!loading && !error && feeds.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {t('transitNetworks.empty')}
        </Typography>
      )}

      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {hasQuery ? (
          <>
            {searchError && (
              <Typography color="error" variant="body2" sx={{ p: 2 }}>
                {t('transitNetworks.searchError')}
              </Typography>
            )}
            {noResults && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {t('transitNetworks.noResults', { query: debouncedQuery })}
              </Typography>
            )}
            {!searchError && resultsList}
          </>
        ) : (
          <List dense disablePadding>
            {feeds.map((feed) => {
              const selected = feed.feedId === selectedFeedId;
              const routes = feed.counts?.routes;
              const stops = feed.counts?.stops;
              const secondary =
                routes != null && stops != null
                  ? t('transitNetworks.counts', {
                      routes: routes.toLocaleString(),
                      stops: stops.toLocaleString(),
                    })
                  : feed.agencyTimezone || '';
              return (
                <ListItemButton
                  key={feed.feedId}
                  selected={selected}
                  onClick={() => onSelectFeed(selected ? null : feed.feedId)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <DirectionsBusIcon fontSize="small" color={selected ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={feed.onestopId || feed.feedId}
                    secondary={secondary}
                    primaryTypographyProps={{ noWrap: true, title: feed.feedId, variant: 'body2' }}
                    secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                  />
                  {selected && <Chip size="small" label={t('transitNetworks.onMap')} color="primary" sx={{ ml: 1 }} />}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      {statusText && !hasQuery && (
        <>
          <Divider />
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>
            {statusText}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default HostedNetworkPickerDrawer;
